import { Heading, Icon, Modal, Text } from "@ttc3k/trekker";
import { NavArrowRight, ShoppingBag } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import { AssetImage, Multilingual } from "gql/generated";
import { MultilingualLangKey } from "shared/types";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import { SmartImage } from "shared/components/atoms";

export type CheckoutLauncherWidget = {
  bookerId: string;
  name?: Multilingual | Partial<Multilingual> | null;
  image?: Partial<AssetImage> | null;
};

const CARD_IMAGE_SIZE = 56;

export type CheckoutLauncherProps = {
  open: boolean;
  onClose: () => void;
  widgets: CheckoutLauncherWidget[];
  lang?: MultilingualLangKey;
  onLaunch: (bookerId: string) => void;
};

/**
 * Intermediate slideout shown when an event (or profile page) has more than
 * one publicly published checkout widget. Modeled on the broadcast
 * `StorefrontOrdersIndex` card-list pattern. Rendered without a document-body
 * portal so it stays inside the caller's shadow-DOM container.
 */
export const CheckoutLauncher = ({
  open,
  onClose,
  widgets,
  lang,
  onLaunch,
}: CheckoutLauncherProps) => {
  const { t } = useTranslation("events", {
    keyPrefix: "WIDGET.CHECKOUT_LAUNCHER",
  });

  return (
    <Modal
      open={open}
      onOpenChange={({ open }: { open: boolean }) => {
        if (!open) onClose();
      }}
      closeTriggerVisual={"light"}
      zIndex={"max"}
    >
      <Flex flexDir="column" gap="400" p="500">
        <Flex flexDir="column" gap="100">
          <Heading visual="h4" color="text.dark">
            {t("TITLE")}
          </Heading>
          <Text visual="bodyRegular" color="text.mid">
            {t("DESCRIPTION")}
          </Text>
        </Flex>

        <Flex flexDir="column" gap="200">
          {widgets.map(widget => (
            <Box
              key={widget.bookerId}
              bg="white"
              border="1px solid"
              borderColor="border.light"
              borderRadius="200"
              p="200"
              cursor="pointer"
              _hover={{ borderColor: "border.mid" }}
              onClick={() => onLaunch(widget.bookerId)}
            >
              <Flex justifyContent="space-between" alignItems="center" gap="200">
                <Flex alignItems="center" gap="200" minW={0}>
                  {widget.image ? (
                    <SmartImage
                      image={widget.image}
                      width={CARD_IMAGE_SIZE}
                      shape="rounded"
                    />
                  ) : (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      w={`${CARD_IMAGE_SIZE}px`}
                      h={`${CARD_IMAGE_SIZE}px`}
                      bg="bg.wa.lightest"
                      borderRadius="100"
                    >
                      <Icon Element={ShoppingBag} color="icon.mid" size="sm" />
                    </Flex>
                  )}
                  <Text
                    visual="bodySemiBold"
                    color="text.dark"
                    lineClamp={2}
                    minW={0}
                  >
                    {widget.name
                      ? getMultilingualString(widget.name as Multilingual, lang)
                      : ""}
                  </Text>
                </Flex>
                <Icon
                  Element={NavArrowRight}
                  color="icon.mid"
                  size="sm"
                  flexShrink={0}
                />
              </Flex>
            </Box>
          ))}
        </Flex>
      </Flex>
    </Modal>
  );
};
